import React, { FC, useRef } from 'react';
import classes from './KTileItem.module.scss';
import {
  defaultHexaPlace,
  defaultRhombusPlace,
  defaultTrianglePlace,
  getContainerStyle,
  getKTileStyle,
  getPivotStyle,
  getTileCoords,
  getTileHeight,
  getTileWidth
} from './KTileAsset';
import KMeeple from './KMeeple';
import KNodeItem from './KNodeItem';
import classNames from 'classnames';
import { getKTileDef } from '../../config/kTileMapConfig';
import { KActionDTO, KTileDTO, Place, Shape } from '@envclient/envengine/src/envs/koenigsburg/KTile';
import { NodeDef } from '../../config/tileMapConfig';
import { KNodeDef, KTileDef } from '@envclient/envengine/src/envs/koenigsburg/KTileGameConfig';
import { KResource } from '@envclient/envengine/src/envs/koenigsburg/KResource';
import { colorMap } from '../ccson/CcsonPlayerItem';
import { KResourceItem } from './KResourceItem';
import { KPlayerResourceItem } from './KPlayerResourceItem';
import { MeepleDTO, MeepleType } from '@envclient/envengine/src/envs/tilebase/Meeple';
import { useTimeout } from 'react-use';
import { CSSPos, Pos } from '../../types/frontendTypes';

export function calculateRotatedPivotedNodePos(
  meepleNode: NodeDef | undefined,
  pivotRot: number,
  rot: number,
  shape: Shape,
  meepleOffset = true
) {
  // rotate first
  const tileWidth = getTileWidth(shape);
  const tileHeight = getTileHeight(shape);
  if (!meepleNode) return;
  const xCentered = (meepleNode.x - 0.5) * tileWidth;
  const yCentered = (meepleNode.y - 0.5) * tileHeight;
  const rad = rot * (Math.PI / 180);
  const xRotated = Math.cos(rad) * xCentered - Math.sin(rad) * yCentered;
  const yRotated = Math.sin(rad) * xCentered + Math.cos(rad) * yCentered;
  // move back center after rotation
  let xPivotCenter = xRotated + tileWidth / 2;
  let yPivotCenter = yRotated + tileHeight / 2;
  // pivot center down for rombus
  if (shape === 'rhombus') {
    xPivotCenter = xPivotCenter - tileWidth / 2;
    yPivotCenter = yPivotCenter - tileHeight;
  } else {
    xPivotCenter = xPivotCenter - tileWidth / 2;
    yPivotCenter = yPivotCenter - tileHeight / 2;
  }
  const pivotRad = pivotRot * (Math.PI / 180);
  const xPivoted = Math.cos(pivotRad) * xPivotCenter - Math.sin(pivotRad) * yPivotCenter;
  const yPivoted = Math.sin(pivotRad) * xPivotCenter + Math.cos(pivotRad) * yPivotCenter;
  // transform back into px coords
  let left;
  let top;
  if (shape === 'rhombus') {
    left = Math.round(xPivoted + tileWidth / 2);
    top = Math.round(yPivoted + tileHeight);
  } else {
    left = Math.round(xPivoted + tileWidth / 2);
    top = Math.round(yPivoted + tileHeight / 2);
  }
  const meepleWidth = 41;
  const meepleHeight = 50;
  // add meeple offset
  if (meepleOffset) {
    left = Math.round(left - meepleWidth / 2);
    top = Math.round(top - meepleHeight / 2);
  }
  return { left, top };
}

interface TileItemProps {
  tile: KTileDTO;
  active: boolean;
  last?: boolean;
  onPlaceClick?: (tile: KTileDTO) => void;
  onTempClick?: (tile: KTileDTO) => void;
  onClick?: (action: KActionDTO) => void;
  onDragStart?: (
    tile: KTileDTO,
    clientX: number,
    clientY: number,
    rect: DOMRect,
    sourceScale: number,
    pointerId: number
  ) => void;
  onMeepleClick?: (meeple: MeepleDTO) => void;
  onResourceClick?: (resource: KResource) => void;
  posActions?: Array<KActionDTO>;
  preferredMeepleType?: MeepleType;
  rotateThreshold?: number;
  selectedAction?: KActionDTO;
  selected?: boolean;
  tempPos?: CSSPos;
  startStyle?: CSSPos;
  dragging?: boolean;
}

const KTileItem: FC<TileItemProps> = (props) => {
  // console.log('KTileItem props', props);
  const { tile, active, last, posActions, selectedAction, selected, tempPos, dragging, rotateThreshold = 1000 } = props;
  // outer .tileItem box ref - this is the element positioned by tempPos, so drag offsets
  // must be measured against it (not the pivoted/rotated .rotCont)
  const tileItemRef = useRef<HTMLDivElement>(null);
  const { place, type, rot, meeple, shape, resource, taxMeeples, mineCount } = tile;

  const defaultPlace: Place =
    shape === 'triangle' ? defaultTrianglePlace : shape === 'rhombus' ? defaultRhombusPlace : defaultHexaPlace;

  //  TODO - add default place.positions on backend
  const tilePlace = place?.positions.length ? place : defaultPlace;
  const tileDef: KTileDef = getKTileDef(type);
  const nodes = tileDef.nodes;
  const wResourceNodes = nodes.filter((node) => node.type === 'wr');

  // early feedback: for a city (hexa), the points currently earnable by dominating it - gates + silver
  // (mineCount) - and who's currently leading it, mirroring the engine's own finalUpdate formula
  // (KTileLogic.finalUpdate: nrOfGates + tile.mineCount, awarded to every tied max-score player).
  const cityScorePoints = shape === 'hexa' ? nodes.filter((node) => node.type === 'r').length + mineCount : 0;
  const cityPlayerResources = tile.playerResource ? Array.from(tile.playerResource.map.values()) : [];
  const cityMaxScore = cityPlayerResources.length ? Math.max(...cityPlayerResources.map((r) => r.score)) : 0;
  const cityLeaders =
    shape === 'hexa' && cityMaxScore > 0 ? cityPlayerResources.filter((r) => r.score === cityMaxScore) : [];

  const tileWidth = getTileWidth(tileDef.shape);
  const tileHeight = getTileHeight(tileDef.shape);

  // For Kburg just rotate actions for now.
  const findNextAction = () => {
    if (!selectedAction || !posActions) return null;
    let index = posActions.indexOf(selectedAction);
    if (index === -1) index = 0;
    index++;
    if (index >= posActions.length) index = 0;
    return posActions[index];
  };

  const getNodeActions = (node: KNodeDef): KActionDTO[] => {
    const { tile, posActions } = props;
    const { rot } = tile;
    if (!posActions) return [];
    return posActions.filter((action) => {
      return action.tile.rot === rot && action.tile.meeple && action.tile.meeple.nodeId === node.id;
    });
  };

  const findClosestPos = (nativeEvent: globalThis.MouseEvent) => {
    const { posActions, tile } = props;
    const { rot } = tile;
    if (!posActions) return null;
    const nodes = tileDef.nodes;
    let minDistance = 1000000;
    let closestNode;

    function distance2(x1: number, y1: number, x2: number, y2: number) {
      return (x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2);
    }

    for (const node of nodes) {
      if (!getNodeActions(node).length) continue;
      const d = distance2(node.x * tileWidth, node.y * tileHeight, nativeEvent.offsetX, nativeEvent.offsetY);
      if (d < minDistance) {
        minDistance = d;
        closestNode = node;
      }
    }
    if (closestNode && minDistance < rotateThreshold) {
      const closestPos = getNodeActions(closestNode);
      console.log('closestPos', closestPos, meeple);
      if (closestPos.length > 1) {
        return closestPos[1];
      }
      return closestPos[0];
    }
    // try to rotate forw
    let nextRotPos = posActions.find((action: KActionDTO) => action.tile.rot > rot);
    //try to rotate backw
    if (!nextRotPos) nextRotPos = posActions.find((action: KActionDTO) => action.tile.rot < rot);
    // return if found
    if (nextRotPos) return nextRotPos;
  };

  const handleClick = () => {
    if (tempPos) {
      props.onPlaceClick?.(tile);
      return;
    }
    if (tile.isTemp && !selected) {
      props.onTempClick?.(tile);
      return;
    }
    if (!props.active || !props.onClick) return;
    // find the closest positioned action based on mouse click
    // const action = findClosestPos(e.nativeEvent);
    const action = findNextAction();
    console.log('nextAction', action?.tile);
    if (action) props.onClick(action);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!props.active || !props.onDragStart) return;
    // measure the outer box (what tempPos positions), not the pivoted .rotCont under the cursor
    const rect = (tileItemRef.current ?? e.currentTarget).getBoundingClientRect();
    // outer box CSS width === getTileWidth(shape), so rect.width/that === the rendered scale (zoom)
    const sourceScale = rect.width / getTileWidth(shape) || 1;
    props.onDragStart(tile, e.clientX, e.clientY, rect, sourceScale, e.pointerId);
  };

  // Block PrismaZoom panning while interacting with a draggable tile.
  // Pointer events and mouse/touch events are separate channels, so stopping
  // propagation on the pointer event alone would not prevent panning.
  const stopDragPan = (e: React.MouseEvent | React.TouchEvent) => {
    if (!props.active || !props.onDragStart) return;
    e.stopPropagation();
  };

  const handleMeepleClick = (meeple: MeepleDTO) => {
    if (!props.active || !props.onMeepleClick) return;
    props.onMeepleClick(meeple);
  };

  const handleResourceClick = (resource: KResource) => {
    if (!props.onResourceClick) return;
    props.onResourceClick(resource);
  };

  const { left, top } = getKTileStyle(tilePlace);

  const coords = getTileCoords(type) as Pos;

  const pivotStyle = getPivotStyle(tilePlace);

  const meepleRot = +pivotStyle.transform.match(/(\d+)/)?.[0] || 0;

  const contStyle = getContainerStyle(coords, rot, tilePlace.shape);

  let pivotedMeeplePos;
  if (meeple) {
    const meepleNode = nodes.find((node) => node.id === meeple.nodeId);
    pivotedMeeplePos = calculateRotatedPivotedNodePos(meepleNode, meepleRot, rot, shape);
  }

  const cityNode = nodes.find((node) => node.type === 'c');
  const cityPos = calculateRotatedPivotedNodePos(cityNode, meepleRot, rot, shape);

  const tileClasses = classNames(classes.tileItem, {
    [classes.hexa]: shape === 'hexa',
    [classes.rhombus]: shape === 'rhombus',
    [classes.triangle]: shape === 'triangle'
  });
  const rotClasses = classNames(classes.rotCont, {
    [classes.hexa]: shape === 'hexa',
    [classes.rhombus]: shape === 'rhombus',
    [classes.triangle]: shape === 'triangle'
  });

  const style: React.CSSProperties = tempPos ? { ...tempPos } : { left, top };

  // place meeple animation
  const [isReady] = useTimeout(1);

  if (!isReady() && selected && props.startStyle) {
    style.left = props.startStyle.left;
    style.top = props.startStyle.top;
  }

  // follow the cursor without the placement easing while being dragged
  if (dragging) {
    style.transition = 'none';
  }

  return (
    <>
      <div ref={tileItemRef} className={tileClasses} style={style}>
        <div className={classes.pivotCont} style={pivotStyle}>
          <div
            className={rotClasses}
            style={contStyle}
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onMouseDown={stopDragPan}
            onTouchStart={stopDragPan}>
            <div className={classes.innerCont}>
              {last && <div className={classes.last}></div>}
              {selected && <div className={classes.act}></div>}
              {wResourceNodes.map((node) => {
                const nodeStyle = {
                  left: node.x * tileWidth,
                  top: node.y * tileHeight
                };
                return <KNodeItem key={node.id} node={node} active={active} nodeStyle={nodeStyle} />;
              })}
            </div>
            {shape === 'triangle' && active && (
              <svg width="100%" height="100%" viewBox="0 0 65 75">
                <polygon points="1,1 1,74 64,37" fillOpacity={0} stroke={selected ? 'red' : 'orange'} strokeWidth="2" />
              </svg>
            )}
            {shape === 'rhombus' && active && (
              <svg width="100%" height="100%" viewBox="0 0 130 75">
                <polygon
                  points="65,1 1,37 65,74 128,37"
                  fillOpacity={0}
                  stroke={selected ? 'red' : 'orange'}
                  strokeWidth="2"
                />
              </svg>
            )}
            {shape === 'hexa' && active && (
              <svg width="100%" height="100%" viewBox="0 0 130 150">
                <polygon
                  points="65,1 1,37 1,112 65,148 129,112 129,37"
                  fillOpacity={0}
                  stroke={selected ? 'red' : 'orange'}
                  strokeWidth="2"
                />
              </svg>
            )}
          </div>
        </div>

        {meeple && (
          <KMeeple
            rotStyle={{}}
            meeple={meeple}
            pos={pivotedMeeplePos}
            selected={selected}
            onClick={handleMeepleClick}
          />
        )}
        {taxMeeples.map((taxMeeple) => {
          const { id, type, nodeId, tileId } = taxMeeple;
          const meepleNode = nodes.find((node) => node.id === taxMeeple.nodeId);
          const taxMeeplePos = calculateRotatedPivotedNodePos(meepleNode, meepleRot, rot, shape);
          return <KMeeple key={id} rotStyle={{}} meeple={taxMeeple} pos={taxMeeplePos} selected={false} />;
        })}
        {resource && <KResourceItem resource={resource} pos={pivotedMeeplePos} onClick={handleResourceClick} />}
        {tile.playerResource && <KPlayerResourceItem playerResource={tile.playerResource} pos={cityPos} />}
        {mineCount > 0 && (
          <div className={classes.mineCountBadge}>
            <span className={classes.mineCountLabel}>{mineCount}</span>
          </div>
        )}
        {cityLeaders.length > 0 && (
          <div className={classes.cityScoreBadgeCont}>
            {cityLeaders.map((r) => (
              <div key={r.id} className={classes.cityScoreBadge} style={{ backgroundColor: colorMap[r.color].code }}>
                <span className={classes.cityScoreLabel}>{cityScorePoints}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(KTileItem);
